import { useSelector } from 'react-redux';
import {
  LOCATION_SEARCH_DATA_LOCAL_STORAGE_KEY,
  ZIPCODE_DATA_LOCAL_STORAGE_KEY,
  // MICROKERNEL_STORE_DEALER_KEY,
  LOCATION_SEARCH_DATA_CHANGE_EVENT,
  PARTNER_KVPS_SYNC_ID,
  PARTNER_ID,
} from '../constants/locationConstants';
import { MARKET } from '../constants/market';
import {
  Coordinates,
  DealershipList,
  LocationSearchData,
  SelectedUserLocation,
  AutocompleteFeature,
} from '../types/locations';
// CHANGE
// import { dealerSave } from '../utils/analyticsEventListeners';
import { DealershipLocation } from '../types/dealershipLocations';
import { PlaceResultType } from '../types/placeResultLocation';
import { selectDealerContext } from '../reducers/dealerContextReducer';

interface MicrokernelDealer {
  dealerName: string;
  dealerPhone: string;
  dealerURL: string;
  kvpsSyncId: string;
  kvpsid: string;
}

type StateListener = (state: MicrokernelDealer | undefined) => void;

type SubscriberFn = (key: string, listener: StateListener) => void;

declare global {
  interface Window {
    microkernel: {
      stateRegistry: {
        subscribeToStore: SubscriberFn;
        unsubscribeFromStore: SubscriberFn;
      };
    };
  }
}

type SaveFnProps = {
  selectedDealershipList: DealershipLocation[];
  radius: number;
  selectedUserLocation: SelectedUserLocation;
  searchMode: 'BY_RADIUS' | 'BY_DEALERS';
};

type SaveFn = (data: SaveFnProps) => void;

export interface UseSelectedDealersResponse {
  saveDealersToLS: (toSave?: LsInterface) => void;
  getSavedDealersFromLS: (dealerContextId: string | null) => LsInterface;
  initialData: LsInterface;
}

type UseSelectedDealers = () => UseSelectedDealersResponse;

export type LsInterface = {
  selectedDealershipList: DealershipList;
  selectedRadius: number;
  selectedUserLocation: SelectedUserLocation | undefined;
  selectedSearchMode: string;
  isPreSetPartner: boolean;
  isPartnerHeader?: boolean;
  invalidDataFromLs?: boolean;
};

const DEFAULT_LOCATION_SEARCH_DATA = {
  selectedDealershipList: [],
  selectedRadius: MARKET === 'CAN' ? 150 : 50,
  selectedUserLocation: undefined,
  selectedSearchMode: 'BY_RADIUS' as const,
  isPreSetPartner: false,
  isPartnerHeader: false,
  invalidDataFromLs: true,
};

const getSavedDealersFromLS = (dealerContextId: string | null): LsInterface => {
  const lsValue = localStorage.getItem(LOCATION_SEARCH_DATA_LOCAL_STORAGE_KEY);

  if (!lsValue) return DEFAULT_LOCATION_SEARCH_DATA;

  try {
    const parsed = JSON.parse(lsValue) as LocationSearchData;

    /**
     * Don't reload a pre-set partner from LS. If the query param is still present
     * we will set it, if not, should will clear it regardless
     */
    if (parsed.isPreSetPartner || parsed.selectedUserLocation === undefined)
      return DEFAULT_LOCATION_SEARCH_DATA;

    let userLocationCenter: PlaceResultType;

    if (
      parsed.selectedUserLocation &&
      Object.keys(parsed.selectedUserLocation?.feature).includes('placeName')
    ) {
      // Previous type of LS interface
      // Convert AutoComplete Feature Type(Mapbox) to PlaceResultType(Google)
      const autoCompleteData: AutocompleteFeature = parsed.selectedUserLocation
        .feature as unknown as AutocompleteFeature;
      userLocationCenter = {
        name: autoCompleteData.placeName,
        geometry: {
          location: {
            lat: autoCompleteData.center.latitude,
            lng: autoCompleteData.center.longitude,
          },
        },
        formattedAddress: autoCompleteData.placeName,
      };
    } else {
      userLocationCenter = parsed.selectedUserLocation?.feature as PlaceResultType;
    }

    const userSelectedLocation: SelectedUserLocation = {
      userCoordinates: parsed.selectedUserLocation?.userCoordinates as Coordinates,
      feature: userLocationCenter,
    };

    if (parsed.mode === 'BY_RADIUS') {
      return {
        selectedSearchMode: 'BY_RADIUS',
        selectedDealershipList:
          parsed.dealershipList ?? DEFAULT_LOCATION_SEARCH_DATA.selectedDealershipList,
        selectedRadius: parsed.radius ?? DEFAULT_LOCATION_SEARCH_DATA.selectedRadius,
        selectedUserLocation:
          userSelectedLocation ?? DEFAULT_LOCATION_SEARCH_DATA.selectedUserLocation,
        isPreSetPartner: false,
        isPartnerHeader: !!PARTNER_ID || !!dealerContextId,
      };
    }

    return {
      selectedSearchMode: 'BY_DEALERS',
      selectedDealershipList:
        parsed.dealershipList ?? DEFAULT_LOCATION_SEARCH_DATA.selectedDealershipList,
      selectedRadius: DEFAULT_LOCATION_SEARCH_DATA.selectedRadius,
      selectedUserLocation:
        userSelectedLocation ?? DEFAULT_LOCATION_SEARCH_DATA.selectedUserLocation,
      isPreSetPartner: parsed.isPreSetPartner ?? DEFAULT_LOCATION_SEARCH_DATA.isPreSetPartner,
      isPartnerHeader: !!PARTNER_ID || !!dealerContextId,
    };
  } catch (e) {
    return DEFAULT_LOCATION_SEARCH_DATA;
  }
};

const saveDealersToLS: (toSave?: LsInterface) => void = (toSave) => {
  // if no data received, then clear key from LS
  if (!toSave) {
    localStorage.removeItem(LOCATION_SEARCH_DATA_LOCAL_STORAGE_KEY);
    const event = new CustomEvent(LOCATION_SEARCH_DATA_CHANGE_EVENT);
    document.dispatchEvent(event);
    return;
  }

  // Store newly selected dealers in LS
  let toSaveLsFormat;
  if (toSave.selectedSearchMode === 'BY_RADIUS') {
    toSaveLsFormat = {
      mode: toSave.selectedSearchMode,
      radius: toSave.selectedRadius,
      dealershipList: toSave.selectedDealershipList,
      selectedUserLocation: toSave.selectedUserLocation,
      isPreSetPartner: false,
      isPartnerHeader: toSave.isPartnerHeader,
    };
  } else {
    toSaveLsFormat = {
      mode: toSave.selectedSearchMode,
      dealershipList: toSave.selectedDealershipList,
      selectedUserLocation: toSave.selectedUserLocation,
      isPreSetPartner: toSave.isPreSetPartner,
      isPartnerHeader: toSave.isPartnerHeader,
    };
  }

  // Store ZIP Code in Local Storage for US-Promotions if it's avaialble otherwise remove the key
  const postalCode = toSave?.selectedUserLocation?.feature?.postalCode;
  if (postalCode) {
    localStorage.setItem(ZIPCODE_DATA_LOCAL_STORAGE_KEY, postalCode);
  } else {
    localStorage.removeItem(ZIPCODE_DATA_LOCAL_STORAGE_KEY);
  }

  localStorage.setItem(LOCATION_SEARCH_DATA_LOCAL_STORAGE_KEY, JSON.stringify(toSaveLsFormat));

  // Dispatch a custom event to notify other microfrontends about the change
  const event = new CustomEvent(LOCATION_SEARCH_DATA_CHANGE_EVENT);
  document.dispatchEvent(event);
};

const useSelectedDealers: UseSelectedDealers = () => {
  const { dealerContextId } = useSelector(selectDealerContext);
  const initialData = !PARTNER_KVPS_SYNC_ID
    ? getSavedDealersFromLS(dealerContextId)
    : {
        ...DEFAULT_LOCATION_SEARCH_DATA,
        selectedDealershipList: [{ kvpsSyncId: PARTNER_KVPS_SYNC_ID, name: 'Pre-set dealer' }],
        selectedSearchMode: 'BY_DEALERS' as const,
        isPreSetPartner: true,
      };

  // If LS data could not be read, clear it
  if (initialData.invalidDataFromLs) saveDealersToLS(initialData);
  return {
    saveDealersToLS,
    initialData,
    getSavedDealersFromLS,
  };
};

export default useSelectedDealers;
